import {
  createMongoAbility,
  MongoAbility,
  AbilityBuilder,
} from "@casl/ability";
import { makeAutoObservable, observable } from "mobx";

type Actions =
  | "manage"
  | "create"
  | "read"
  | "update"
  | "delete"
  | "execute"
  | "publish"
  | "configure"
  | "import"
  | "export";
type Subjects =
  | "all"
  | "organization"
  | "workflow"
  | "campaign"
  | "channel"
  | "cdp"
  | "integration"
  | "conversation"
  | "user"
  | "role"
  | "analytics"
  | "billing"
  | "apiKey"
  | "webhook";

export type AppAbility = MongoAbility<[Actions, Subjects]>;

// Map capability role resources to CASL subjects
const RESOURCE_TO_SUBJECT: Record<string, Subjects | null> = {
  workflow: "workflow",
  campaign: "campaign",
  channel: "channel",
  cdp: "cdp",
  organization: "organization",
  conversation: "conversation",
  integration: "integration",
  user: "user",
  role: "role",
  billing: "billing",
  apikey: "apiKey",
  "api-key": "apiKey",
  webhook: "webhook",
};

// Map capability role actions to CASL actions
const ACTION_TO_CASL_ACTION: Record<string, Actions | null> = {
  view: "read",
  read: "read",
  create: "create",
  update: "update",
  delete: "delete",
  manage: "manage",
  execute: "execute",
  publish: "publish",
  configure: "configure",
  import: "import",
  export: "export",
};

// Backward compatibility: Map membership roles to capability roles
// This is used when API fallback returns membership roles
const MEMBERSHIP_ROLE_MAPPING: Record<string, string> = {
  "org-owner": "owner",
  "org-admin": "admin",
  "org-supervisor": "supervisor",
  "org-agent": "agent",
  "org-developer": "developer",
  "org-marketing": "marketing",
  "org-reporter": "reporter",
  ORGANIZATION_OWNER: "owner",
  ORGANIZATION_ADMIN: "admin",
  ORGANIZATION_SUPERVISOR: "supervisor",
  ORGANIZATION_AGENT: "agent",
  ORGANIZATION_DEVELOPER: "developer",
  ORGANIZATION_MARKETING: "marketing",
  ORGANIZATION_REPORTER: "reporter",
  owner: "owner",
  admin: "admin",
  supervisor: "supervisor",
  agent: "agent",
  developer: "developer",
  marketing: "marketing",
  reporter: "reporter",
};

export default class CaslStore {
  ability: AppAbility;
  isLoading: boolean = true; // Track if permissions are still being loaded

  constructor() {
    this.ability = this.defineAbilitiesFor([]); // default: no permissions
    makeAutoObservable(this, {
      ability: observable.ref, // Use ref since ability is a complex object
    });
  }

  defineAbilitiesFor(roles: string[]): AppAbility {
    const { can, cannot, build } = new AbilityBuilder<
      MongoAbility<[Actions, Subjects]>
    >(createMongoAbility);

    // If empty roles, return empty ability
    if (!roles || roles.length === 0) {
      console.warn("⚠️ No roles provided to CASL, granting no permissions");
      return build();
    }

    // Check if roles are capability roles (format: resource-action) or membership roles
    const hasCapabilityRoles = roles.some(r =>
      /^[a-z]+-[a-z]+/.test(r.toLowerCase()),
    );

    if (hasCapabilityRoles) {
      // Process capability roles
      this.mapCapabilityRolesToCASL(roles, can, cannot);
    } else {
      // Backward compatibility: Process membership roles
      console.warn(
        "⚠️ Using backward compatibility mode: membership roles detected, mapping to capabilities",
      );
      const normalizedRoles = roles.map(
        r => MEMBERSHIP_ROLE_MAPPING[r] || r.toLowerCase(),
      );
      this.mapMembershipRolesToCASL(normalizedRoles, can, cannot);
    }

    return build();
  }

  /**
   * Map capability roles directly to CASL permissions
   * Capability roles format: {resource}-{action} (e.g., workflow-view, campaign-create)
   */
  private mapCapabilityRolesToCASL(
    capabilityRoles: string[],
    can: (action: Actions, subject: Subjects) => void,
    cannot: (action: Actions, subject: Subjects) => void,
  ): void {
    // Normalize to lowercase for comparison
    const normalizedRoles = capabilityRoles.map(r => r.toLowerCase());
    const hasOrgManage = normalizedRoles.includes("organization-manage");
    const hasOrgDelete = normalizedRoles.includes("organization-delete");
    const hasAnyViewCapability = normalizedRoles.some(
      r => r.endsWith("-view") || r.endsWith("-read"),
    );

    // If user has organization-manage capability, grant full access (owner/admin permissions)
    // This gives them "manage all" which allows access to all resources
    // Note: If they don't have organization-delete, we'll add a restriction below
    if (hasOrgManage) {
      can("manage", "all");
      console.debug(
        "✅ Organization-manage capability detected → granting manage all (full access)",
      );
      // Still process individual roles for logging/debugging, but "manage all" covers everything
    }

    for (const role of capabilityRoles) {
      // Parse capability role: {resource}-{action} or {resource}-{action}-{modifier}
      const parts = role.toLowerCase().split("-");
      if (parts.length < 2) {
        console.warn(`⚠️ Invalid capability role format: ${role}, skipping`);
        continue;
      }

      // Handle multi-part resources (e.g., api-key, apiKey)
      let resource: string;
      let action: string;

      // Check for known multi-part resources
      if (parts[0] === "api" && parts[1] === "key") {
        resource = "apikey";
        action = parts.slice(2).join("-") || "view";
      } else {
        resource = parts[0];
        action = parts.slice(1).join("-");
      }

      // Map resource to CASL subject
      const subject = RESOURCE_TO_SUBJECT[resource];
      if (!subject) {
        console.warn(
          `⚠️ Unknown resource in capability role: ${resource} (from ${role}), skipping`,
        );
        continue;
      }

      // Map action to CASL action
      // Handle multi-part actions (e.g., channel-configure)
      let caslAction: Actions | null = null;

      // Check full action first (e.g., "configure")
      if (ACTION_TO_CASL_ACTION[action]) {
        caslAction = ACTION_TO_CASL_ACTION[action];
      } else {
        // Try first part of action
        const firstAction = action.split("-")[0];
        caslAction = ACTION_TO_CASL_ACTION[firstAction] || null;
      }

      if (!caslAction) {
        console.warn(
          `⚠️ Unknown action in capability role: ${action} (from ${role}), skipping`,
        );
        continue;
      }

      // Grant permission
      can(caslAction, subject);
      console.debug(
        `✅ Mapped capability role: ${role} → can('${caslAction}', '${subject}')`,
      );
    }

    // Special handling for organization-delete
    // organization-manage without organization-delete means can't delete
    if (hasOrgManage && !hasOrgDelete) {
      cannot("delete", "organization");
      console.log(
        "🔒 Organization manage without delete: cannot delete organization",
      );
    }

    // Analytics access: if user can view any resource, they can view analytics
    if (hasAnyViewCapability) {
      can("read", "analytics");
      console.debug(
        "✅ Granting analytics read access (user has view capabilities)",
      );
    }
  }

  /**
   * Backward compatibility: Map membership roles to CASL permissions
   * This is used when API fallback returns membership roles instead of capability roles
   */
  private mapMembershipRolesToCASL(
    normalizedRoles: string[],
    can: (action: Actions, subject: Subjects) => void,
    cannot: (action: Actions, subject: Subjects) => void,
  ): void {
    // Owner: Full access to everything
    if (normalizedRoles.includes("owner")) {
      can("manage", "all");
      console.log("✅ Membership role 'owner' → manage all");
      return;
    }

    // Admin: Full access except organization deletion
    if (normalizedRoles.includes("admin")) {
      can("manage", "all");
      cannot("delete", "organization");
      console.log(
        "✅ Membership role 'admin' → manage all except delete organization",
      );
      return;
    }

    // Supervisor permissions
    if (normalizedRoles.includes("supervisor")) {
      can("read", "organization");
      can("read", "workflow");
      can("create", "workflow");
      can("update", "workflow");
      can("execute", "workflow");
      can("read", "campaign");
      can("create", "campaign");
      can("update", "campaign");
      can("execute", "campaign");
      can("read", "channel");
      can("create", "channel");
      can("update", "channel");
      can("read", "cdp");
      can("create", "cdp");
      can("update", "cdp");
      can("read", "conversation");
      can("read", "analytics");
      can("read", "integration");
      can("read", "user");
      console.log("✅ Membership role 'supervisor' → mapped to capabilities");
    }

    // Agent permissions
    if (normalizedRoles.includes("agent")) {
      can("read", "organization");
      can("read", "workflow");
      can("execute", "workflow");
      can("read", "campaign");
      can("read", "channel");
      can("read", "cdp");
      can("read", "conversation");
      can("update", "conversation");
      can("read", "analytics");
      can("read", "integration");
      console.log("✅ Membership role 'agent' → mapped to capabilities");
    }

    // Developer permissions
    if (normalizedRoles.includes("developer")) {
      can("read", "organization");
      can("manage", "workflow");
      can("publish", "workflow");
      can("read", "campaign");
      can("create", "campaign");
      can("update", "campaign");
      can("read", "channel");
      can("create", "channel");
      can("update", "channel");
      can("configure", "channel");
      can("read", "cdp");
      can("create", "cdp");
      can("update", "cdp");
      can("read", "conversation");
      can("read", "analytics");
      can("read", "integration");
      can("read", "user");
      console.log("✅ Membership role 'developer' → mapped to capabilities");
    }

    // Marketing permissions
    if (normalizedRoles.includes("marketing")) {
      can("read", "organization");
      can("read", "workflow");
      can("read", "campaign");
      can("manage", "campaign");
      can("execute", "campaign");
      can("read", "channel");
      can("create", "channel");
      can("update", "channel");
      can("read", "cdp");
      can("create", "cdp");
      can("update", "cdp");
      can("read", "conversation");
      can("read", "analytics");
      can("read", "integration");
      console.log("✅ Membership role 'marketing' → mapped to capabilities");
    }

    // Reporter permissions (read-only)
    if (normalizedRoles.includes("reporter")) {
      can("read", "organization");
      can("read", "workflow");
      can("read", "campaign");
      can("read", "channel");
      can("read", "cdp");
      can("read", "conversation");
      can("read", "analytics");
      can("read", "integration");
      can("read", "user");
      console.log("✅ Membership role 'reporter' → mapped to capabilities");
    }

    // Integration-specific permissions (for all roles that can manage integrations)
    if (
      normalizedRoles.some(r =>
        ["owner", "admin", "supervisor", "developer"].includes(r),
      )
    ) {
      can("read", "integration");
      can("create", "integration");
      can("update", "integration");
      can("delete", "integration");
      can("configure", "integration");
      can("read", "apiKey");
      can("create", "apiKey");
      can("update", "apiKey");
      can("delete", "apiKey");
      can("read", "webhook");
      can("create", "webhook");
      can("update", "webhook");
      can("delete", "webhook");
    }

    // Billing permissions (owner and admin only)
    if (normalizedRoles.some(r => ["owner", "admin"].includes(r))) {
      can("read", "billing");
      can("update", "billing");
      can("manage", "billing");
    }

    // User and role management (owner and admin only)
    if (normalizedRoles.some(r => ["owner", "admin"].includes(r))) {
      can("read", "user");
      can("create", "user");
      can("update", "user");
      can("delete", "user");
      can("read", "role");
      can("create", "role");
      can("update", "role");
      can("delete", "role");
    }
  }

  setRoles(roles: string[]) {
    this.ability = this.defineAbilitiesFor(roles);
    this.isLoading = false; // Permissions have been loaded
    console.debug(
      "🔐 CaslStore: Permissions updated, isLoading:",
      this.isLoading,
    );
  }

  setRole(role: string) {
    this.setRoles([role]);
  }

  setLoading(loading: boolean) {
    this.isLoading = loading;
  }
}
